import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { PublicLayout, AdSlot, Section } from "@/components/PublicLayout";
import { ARTICLES, findArticle } from "@/content/articles";

export function ResourcesIndex() {
  return (
    <PublicLayout wide eyebrow="Educational resources" title="Application Guides for South African Students"
      intro="Free, plain-language guides on APS scores, university and TVET college applications, funding and planning — written to help you make confident decisions.">
      <div className="grid sm:grid-cols-2 gap-5" data-testid="articles-grid">
        {ARTICLES.map((a, i) => (
          <Link key={a.slug} to={`/resources/${a.slug}`} data-testid={`article-card-${a.slug}`}
            className="group bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]">
            <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#B8860B]">{String(i + 1).padStart(2, "0")} · {a.category}</span>
            <h2 className="font-display text-lg font-bold text-[#0B2447] mt-2 leading-snug">{a.title}</h2>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">{a.summary}</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B2447] mt-4">Read guide <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
          </Link>
        ))}
      </div>
      <AdSlot id="resources" />
    </PublicLayout>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const a = findArticle(slug);
  if (!a) return <Navigate to="/resources" replace />;
  const others = ARTICLES.filter((x) => x.slug !== slug).slice(0, 3);
  return (
    <PublicLayout eyebrow={a.category} title={a.title} intro={a.summary}>
      <article data-testid="article-body">
        {a.sections.map((s, i) => (
          <div key={s.h}>
            <Section title={s.h}>
              {(s.p || []).map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
              {s.list && <ul className="list-disc pl-5 space-y-2">{s.list.map((li) => <li key={li}>{li}</li>)}</ul>}
            </Section>
            {i === 1 && <AdSlot id="article-mid" />}
          </div>
        ))}
        <p className="mt-10 text-xs text-slate-400">This guide is general information only. Requirements, fees and dates change — always confirm with the official institution or NSFAS before applying.</p>
      </article>
      <div className="mt-12 rounded-2xl bg-[#0B2447] text-white p-6" data-testid="article-cta">
        <p className="font-display text-lg font-bold">Want help with your application?</p>
        <p className="text-sm text-white/75 mt-1">Enter your marks, see the courses you qualify for and let our team apply for you.</p>
        <Link to="/register" className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-[#F5C32C] text-[#0B2447] font-bold text-sm px-5 py-2.5" data-testid="article-cta-btn">Get started <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="mt-12">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2"><BookOpen className="w-4 h-4" /> More guides</p>
        <div className="mt-3 space-y-2">
          {others.map((o) => <Link key={o.slug} to={`/resources/${o.slug}`} className="block text-[#0B2447] font-medium hover:underline" data-testid={`related-${o.slug}`}>{o.title}</Link>)}
        </div>
      </div>
    </PublicLayout>
  );
}
